import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '@/shared/components/TextField/TextField'
import {
  currencySymbol,
  formatPrice,
  formatPriceForInput,
  majorToMinor
} from '@/shared/utils/price'
import { TYPICAL_REWARD_POINTS } from '../../config'
import { LoyaltyRules as LoyaltyRulesModel } from '../../types/loyalty.model'
import styles from './LoyaltyRules.module.scss'

interface LoyaltyRulesProps {
  rules: LoyaltyRulesModel
  onChange: (rules: Partial<LoyaltyRulesModel>) => void
  disabled?: boolean
}

/**
 * The numbers that define how points are earned: the rate, the floor a bill has
 * to clear, and the one-off bonus for leaving contact details.
 *
 * Point validity is not among them: it is fixed at twelve months from the last
 * visit (`POINTS_VALIDITY_MONTHS`), because it is a promise made to the guest
 * and not something a venue should be able to shorten retroactively.
 */
function LoyaltyRules(props: LoyaltyRulesProps) {
  const { rules, onChange, disabled } = props
  const { t } = useTranslation()

  const handlePointsChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const parsed = parseInt(event.target.value, 10)
    onChange({
      pointsPerMajorUnit: Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
    })
  }

  const handleContactBonusChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const parsed = parseInt(event.target.value, 10)
    onChange({
      contactBonusPoints: Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
    })
  }

  const handleMinimumChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Amounts cross this app in minor units (§7). `majorToMinor` also normalises
    // the comma Polish keyboards produce, which `parseFloat` would truncate at.
    onChange({ minimumBillMinor: majorToMinor(event.target.value) ?? 0 })
  }

  /*
    What the rate means in money, so a threshold is not set blind: at 1 point per
    unit a 200-point reward costs the guest 200 units of spend, at 2 it costs
    100. Computed in minor units and formatted like every other amount, because
    the venue's currency decides both the symbol and the number of decimals.
  */
  const exampleSpend =
    rules.pointsPerMajorUnit > 0
      ? majorToMinor(TYPICAL_REWARD_POINTS / rules.pointsPerMajorUnit)
      : undefined

  return (
    <div className={styles.rulesRow}>
      <div className={styles.ruleField}>
        <TextField
          label={t('LOYALTY.RULES.POINTS_PER_UNIT', {
            currency: currencySymbol()
          })}
          type="number"
          onlyInteger
          focused
          disabled={disabled}
          value={String(rules.pointsPerMajorUnit)}
          onChange={handlePointsChange}
        />
        <p className={`body-small ${styles.ruleHint}`}>
          {exampleSpend === undefined
            ? t('LOYALTY.RULES.POINTS_HINT_ZERO', {
                currency: currencySymbol()
              })
            : t('LOYALTY.RULES.POINTS_HINT', {
                points: TYPICAL_REWARD_POINTS,
                amount: formatPrice(exampleSpend)
              })}
        </p>
      </div>
      <div className={styles.ruleField}>
        <TextField
          label={t('LOYALTY.RULES.MINIMUM_BILL')}
          focused
          disabled={disabled}
          endAdornment={currencySymbol()}
          value={formatPriceForInput(rules.minimumBillMinor)}
          onChange={handleMinimumChange}
        />
      </div>
      <div className={styles.ruleField}>
        <TextField
          label={t('LOYALTY.RULES.CONTACT_BONUS')}
          type="number"
          onlyInteger
          focused
          disabled={disabled}
          value={String(rules.contactBonusPoints)}
          onChange={handleContactBonusChange}
        />
        {/*
          Zero is a real setting here, not an empty field, so it gets its own
          line: it is the difference between a programme that asks a guest for
          their e-mail and one that never does.
        */}
        <p className={`body-small ${styles.ruleHint}`}>
          {rules.contactBonusPoints > 0
            ? t('LOYALTY.RULES.CONTACT_BONUS_HINT', {
                count: rules.contactBonusPoints
              })
            : t('LOYALTY.RULES.CONTACT_BONUS_HINT_OFF')}
        </p>
      </div>
    </div>
  )
}

export default LoyaltyRules
